import {Injectable} from '@angular/core';
import {ApiHelperService} from "../helpers/api-helper.service";
import {firstValueFrom, Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import {DecodedJwt} from '../../shared/models';
import {JwtHelperService} from '@auth0/angular-jwt';
import {UserSessionService} from './user-session.service';
import {KeycloakPkceService} from './keycloak-pkce.service';

@Injectable({
    providedIn: 'root'
})
export class DpfService {

    constructor(
        private api: ApiHelperService,
        private userSession: UserSessionService,
        private keycloakPkce: KeycloakPkceService,
    ) {
    }

    async login(body: any): Promise<any> {
        return await firstValueFrom(this.api.create(environment.LOGIN, body));
    }

    async loginWithGoogle(body: any): Promise<any> {
        return await firstValueFrom(this.api.create(environment.LOGIN_WITH_GOOGLE, body));
    }

    /** Calls the logout API, then always wipes the user's browser data and reloads home (even if the API fails). */
    async logout(): Promise<void> {
        try {
            await firstValueFrom(this.api.create(environment.LOGOUT));
        } finally {
            this.userSession.clearAndReload();
        }
    }

    /**
     * Logs out like logout() (the API also ends the Keycloak session, so Keycloak asks for an account again),
     * then opens the Keycloak login at once; once logged in the user comes back to `returnUrl`.
     */
    async switchAccount(returnUrl: string): Promise<void> {
        try {
            await firstValueFrom(this.api.create(environment.LOGOUT));
        } finally {
            this.userSession.clear();
            await this.keycloakPkce.startLogin(returnUrl);
        }
    }

    async authCheck(): Promise<any> {
        const res = await firstValueFrom(this.api.create(environment.AUTH_CHECK));
        if (res?.status) {
            localStorage.setItem('jwt', res.data.jwt);
            const helper = new JwtHelperService();
            const decoded = helper.decodeToken<DecodedJwt>(res.data.jwt);
            localStorage.setItem('userName', decoded?.name);
            localStorage.setItem('userId', String(decoded?.user_id));
            // const permissions = JSON.parse(decoded.permissions);
            // const generalPermissions: Array<any> =
            //     permissions.generalPermissions;
        }
        return res;
    }

    register(body: any): Observable<any> {
        return this.api.create(environment.REGISTER, body);
    }

    newRegister(body: any, params: any): Observable<any> {
        return this.api.create(environment.NEW_REGISTER, body, params);
    }

    completeProfile(body: any): Observable<any> {
        return this.api.update(environment.COMPLETE_PROFILE, body);
    }

    async checkProfile(): Promise<any> {
        return await firstValueFrom(this.api.getAll(environment.CHECK_PROFILE));
    }

    resendOtp(params?: any): Observable<any> {
        return this.api.getAll(environment.RESEND_OTP, params);
    }

    verifyOtp(body: any): Observable<any> {
        return this.api.create(environment.VERIFY_OTP, body);
    }

    submitGeneralInquiry(body: any) {
        return this.api.create(environment.POST_GENERAL_INQUIRY, body);
    }

    getAllUniversity(params?: any) {
        return this.api.getAll(environment.GET_ALL_UNIVERSITY, params);
    }

    exchangeKeycloakCode(body: { code: string; codeVerifier: string }): Observable<any> {
        return this.api.create(environment.KEYCLOAK_EXCHANGE_PATH, body);
    }

    subscribe(body: any) {
        return this.api.create(environment.SUBSCRIBE_EMAIL, body);
    }
}
